"use client";

import React from "react";
import { PhoneCall, Clock, ShieldCheck, ArrowRight, Wrench, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const trustBadges = [{
  icon: Clock,
  title: "24/7 Immediate Dispatch",
  description: "On-call day and night across Jonesboro"
}, {
  icon: ShieldCheck,
  title: "Licensed & Insured",
  description: "Verified master plumbing technicians"
}, {
  icon: Wrench,
  title: "Upfront Honest Pricing",
  description: "Clear diagnostics before repairs begin"
}];
export default function EmergencyHero() {
  return <section id="emergency-plumber-in-jonesboro-ga" data-nav="dark" className="relative min-h-[90vh] lg:min-h-[94vh] flex items-center justify-center bg-background text-foreground overflow-hidden">
      {/* Background Media with Gradient Overlays */}
      <div className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/jonesboro_emergency_plumber_technician_repair.webp)]">
        <div className="absolute inset-0 bg-foreground/85 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/75 to-foreground/50" />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground via-foreground/80 to-transparent" />
      </div>

      <div className="relative z-10 w-full max-w-[1240px] mx-auto px-5 sm:px-8 lg:px-10 py-20 sm:py-24 md:py-28 lg:py-32 flex flex-col justify-between">
        <div className="max-w-3xl">
          {/* Eyebrow / Status Pill */}
          <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-accent/20 border border-accent/40 backdrop-blur-md mb-6 sm:mb-8 animate-fade-in">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent" />
            </span>
            <span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-accent">
              24/7 Emergency Dispatch Active
            </span>
            <span className="hidden sm:inline-block text-background/40">|</span>
            <span className="hidden sm:inline-flex items-center gap-1 text-xs text-background font-medium">
              <MapPin className="size-3 text-accent" />
              Jonesboro, GA & Surrounding Areas
            </span>
          </div>

          {/* Primary H1 Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-background tracking-tight leading-[1.08] font-default mb-6">
            Emergency Plumber in Jonesboro, GA
          </h1>

          {/* Subheading */}
          <p className="text-lg sm:text-xl md:text-2xl text-background/90 font-normal leading-relaxed max-w-2xl mb-8 sm:mb-10">
            Water leaks, burst pipes, and sewer backups cannot wait. Our dedicated local team is ready 24/7 to solve your plumbing crisis with immediate response and upfront pricing.
          </p>

          {/* Dual Action Emergency CTAs */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 sm:gap-5 mb-12 sm:mb-16">
            <Button asChild size="lg" className="h-14 sm:h-16 px-8 rounded-full bg-accent text-accent-foreground font-bold text-base sm:text-lg shadow-xl shadow-accent/20 hover:brightness-105 active:scale-98 transition-[transform,filter,box-shadow]">
              <a href="tel:4708479076" className="flex items-center justify-center gap-3">
                <PhoneCall className="size-5 sm:size-6 shrink-0 animate-bounce" />
                <span>Call Now: (470) 847-9076</span>
              </a>
            </Button>

            <Button asChild size="lg" variant="outline" className="h-14 sm:h-16 px-8 rounded-full bg-background/10 hover:bg-background/20 text-background border-background/30 backdrop-blur-md font-semibold text-base sm:text-lg shadow-lg active:scale-98 transition-[transform,background-color,border-color]">
              <Link to="/contact/" className="flex items-center justify-center gap-2">
                <span>Request Service</span>
                <ArrowRight className="size-5 shrink-0" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Rapid Trust / Commitment Strip */}
        <div className="pt-8 border-t border-background/20 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
          {trustBadges.map((badge, i) => {
          const Icon = badge.icon;
          return <div key={badge.title} data-index={i} className="flex items-start gap-4 p-4 rounded-xl bg-background/5 backdrop-blur-xs border border-background/10 hover:border-accent/40 transition-[border-color,background-color]">
                <div className="w-12 h-12 rounded-xl bg-accent/15 border border-accent/30 flex items-center justify-center shrink-0">
                  <Icon className="size-6 text-accent" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-background font-default tracking-normal">
                    {badge.title}
                  </h2>
                  <p className="text-sm text-background/75 mt-0.5 font-normal">
                    {badge.description}
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}